
// Packages
import { defineComponent, PropType } from 'vue';
import { Portal } from 'portal-vue';

// Helpers
import { formatPrice } from '@white-label-helper/helper-payment';
import {
  parseExtras,
  parseTaxesAndFees,
} from '@white-label-helper/data-parsers-summary';
import { getAppVariable } from '@white-label-helper/get-app-variable';

// Constants
import { BOOKING_STATUSES, FEATURE_FLAGS } from '@white-label-configuration/constants';

// Stores
import { mapState } from 'pinia';
import { useManageBookingStore } from '@white-label-store/manage-booking';
import useDepositStore from '@white-label-store/deposits';

// Types
import type { ManageBookingParsedState as State } from '@white-label-types/stores';
import type {
  SummaryExtras,
  SummaryDiscount,
} from '@white-label-types/summary';
import type { SummaryProps } from '@white-label-types/component';

// Components
import ManageBookingOrderDetails from '../manage-booking-order-details/manage-booking-order-details.vue';
import ManageBookingButtonBlock from '../manage-booking-button-block/manage-booking-button-block.vue';
import PaymentSummary from '../payment-summary/payment-summary.vue';
import StickySidebar from '../sticky-sidebar/sticky-sidebar.vue';

export default defineComponent({
  name: 'ManageBookingMainSection',
  components: {
    MBOrderDetails: ManageBookingOrderDetails,
    MBButtonBlock: ManageBookingButtonBlock,
    StickySidebar,
    PaymentSummary,
    Portal,
  },
  props: {
    orderData: {
      type: Object as PropType<State>,
      required: true,
    },
    modifications: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    ...mapState(useManageBookingStore,
      [
        'readDiscount',
        'readIsCancelled',
        'readIsPastBooking',
        'readIsConfirmed',
        'readIsInProgress',
        'readProductTypesCategories',
        'readManageBookingItems',
      ]
    ),
    discount(): SummaryDiscount | null {
      return this.readDiscount;
    },

    isCancelled(): boolean {
      return this.readIsCancelled;
    },

    isPastBooking(): boolean {
      return this.readIsPastBooking;
    },

    isConfirmed(): boolean {
      return this.readIsConfirmed;
    },

    isOrderItemsCancelledOrPastBooking(): boolean {
      const items = this.readManageBookingItems;
      return items.every(
        (item) =>
          item.status === BOOKING_STATUSES.CANCELLED ||
          item.status === BOOKING_STATUSES.PAST_BOOKING
      );
    },

    isInProgress(): boolean {
      return this.readIsInProgress;
    },

    /**
     * The price of the item when being refunded
     * @returns The price formated or an empty string
     */
    orderTotalFormatted(): string | Intl.NumberFormat {
      if (this['hasDeposit']) {
        return this['hasDeposit'].payNow;
      }

      return typeof this.orderData.orderTotals?.total === 'number'
        ? formatPrice(this.orderData.orderTotals.total)
        : '';
    },

    itemSubtotalFormatted(): string | Intl.NumberFormat {
      return typeof this.orderData.totals?.subtotal === 'number'
        ? formatPrice(this.orderData.totals.subtotal)
        : '';
    },

    itemTotalFormatted(): string | Intl.NumberFormat {
      return typeof this.orderData.totals?.total === 'number'
        ? formatPrice(this.orderData.totals.total)
        : '';
    },

    taxes(): ReturnType<typeof parseTaxesAndFees> {
      return parseTaxesAndFees(this.orderData.taxes);
    },

    fees(): ReturnType<typeof parseTaxesAndFees> {
      return parseTaxesAndFees(this.orderData.fees);
    },

    extras(): SummaryExtras | {} {
      return this.orderData.cancellationProtection && !this.isOrderCancelled
        ? parseExtras(this.orderData.cancellationProtection)
        : {};
    },

    isOrderCancelled(): boolean {
      return this.orderData.status === BOOKING_STATUSES.CANCELLED;
    },

    isOrderPartiallyCancelled(): boolean {
      return !this.isOrderCancelled && this.orderData.items.some(
          (item) => item.status === 'cancelled'
        );
    },

    cancellationProtectionTotalFormatted(): string | Intl.NumberFormat {
      return this.isOrderCancelled &&
        this.orderData?.cancellationProtection?.totals?.total
        ? formatPrice(this.orderData?.cancellationProtection?.totals?.total)
        : '';
    },

    amendmentFeeFormatted(): string | Intl.NumberFormat {
      const { amendmentFee } = this.orderData;
      return typeof amendmentFee === 'number' && amendmentFee > 0
        ? formatPrice(amendmentFee)
        : '';
    },

    priceDifference(): string {
      return String(this.orderData.orderTotals?.cancellation_refunded_total || 0);
    },

    priceDifferenceFormatted(): string | Intl.NumberFormat {
      return (this.isOrderCancelled || this.isOrderPartiallyCancelled)
        ? formatPrice(this.priceDifference)
        : '';
    },

    cancellationFeeFormatted(): string | Intl.NumberFormat {
      return this.isOrderCancelled
        ? formatPrice(this.orderData.orderTotals?.cancellation_fee_total_formatted?.without_currency || 0)
        : '';
    },

    valueAfterRefundWithoutCancellation(): string {
      if (this.isOrderCancelled) return '';
      let total = this.orderData.orderTotals?.total ?? 0;

      if (this.hasDeposit) {
        total = this.orderData.payable.pay_now;
      }

      const { totalRefunded } = this.orderData;
      if (totalRefunded && totalRefunded > 0) {
        return `${formatPrice(total - totalRefunded)}`;
      }
      return '';
    },

    /**
     * Checks if there is a deposit but checking for the existence of a null
     * value or not
     *
     * @returns Returns true if there is a deposit, otherwise false.
     */
    hasDeposit(): false | { payNow: string; payOnArrival: string } {
      const depositOnly = useDepositStore();
      if (depositOnly.cartTotalDepositAmount === null) {
        return false;
      }

      return depositOnly.cartTotalDepositAmount;
    },

    summaryProps(): SummaryProps {
      const cancellationFee =
        this.orderData.cancellationInfo?.cancellation_fee || 0;
      const totalRefunded = this.orderData.totalRefunded || 0;
      const amendmentFee =
        typeof this.amendmentFee === 'number' && this.amendmentFee > 0
          ? formatPrice(this.amendmentFee)
          : null;

      return {
        hasPaid: true,
        orderName: this.orderData.parkingName || '',
        orderTotal: this.orderTotalFormatted,
        itemSubtotal: this.itemSubtotalFormatted,
        itemTotal: this.itemTotalFormatted,
        taxes: this.taxes,
        fees: this.fees,
        extras: this.extras,
        cancellationProtection: this.cancellationProtectionTotalFormatted,
        isRefund: this.isOrderCancelled,
        priceDifference: formatPrice(totalRefunded),
        cancellationFee: this.isOrderCancelled
          ? formatPrice(cancellationFee)
          : '',
        discount: this.discount,
        amendmentFee,
        valueAfterRefundWithoutCancellation:
          this.valueAfterRefundWithoutCancellation,
        itemTotalOld: this.orderData?.orderTotals?.total_without_discount
          ? formatPrice(this.orderData.orderTotals.total_without_discount)
          : '',
        bookingFee: this.orderData?.bookingFee?.is_enabled
          ? formatPrice(this.orderData.bookingFee.amount)
          : '',
      };
    },

    isMultiBasket(): boolean {
      return !!getAppVariable('is_multi_basket');
    },

    bundle() {
      return this.orderData.bundle;
    },
  },

  methods: {
    isProductTypeIncluded(key: string): boolean {
      return this.readProductTypesCategories.includes(key);
    },
  },
});
